import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { useConfirmation } from '@/components/shared/ZCard';
import ZLoadingBar from '@/components/shared/ZLoadingBar';

export default function Auth() {
  const feedback = useConfirmation();
  const [passphrase, setPassphrase] = useState('');
  const [isSetup, setIsSetup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backendDown, setBackendDown] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectAfterAuth = () => {
    const from = searchParams.get('from');
    navigate(from ? decodeURIComponent(from) : '/');
  };

  const checkBackend = () => {
    setBackendDown(false);
    setIsSetup(null);
    api.get('/auth/check', { timeout: 8000 })
      .then(r => setIsSetup(r.data.setup))
      .catch(() => setBackendDown(true));
  };

  useEffect(() => {
    if (isAuthenticated) { redirectAfterAuth(); return; }
    checkBackend();
  }, [isAuthenticated]); // eslint-disable-line

  // Auto-retry every 4s when backend is unreachable
  useEffect(() => {
    if (!backendDown) return;
    const t = setTimeout(checkBackend, 4000);
    return () => clearTimeout(t);
  }, [backendDown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passphrase.trim()) return;
    setLoading(true);
    try {
      const endpoint = isSetup ? '/auth/login' : '/auth/setup';
      const { data } = await api.post(endpoint, { passphrase: passphrase.trim() });
      login(data.token);
      redirectAfterAuth();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Authentication failed';
      feedback.fail(msg);
      setPassphrase('');
    } finally {
      setLoading(false);
    }
  };

  if (backendDown) return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center gap-4">
      <div className="w-8 h-8 rounded-lg bg-white/[0.06]" />
      <p className="text-[#b0b4c0] text-sm font-light">Backend warming up…</p>
      <button
        onClick={checkBackend}
        className="mt-2 px-5 py-2 bg-white/[0.05] border border-white/[0.06] text-[#b0b4c0] hover:text-[#f0f0f5] rounded-full text-xs font-mono tracking-wider transition-colors"
      >
        Retry
      </button>
    </div>
  );

  if (isSetup === null) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div style={{ width: 200 }}><ZLoadingBar accent="#6B35B8" loading={true} /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center p-8" data-testid="auth-page">
      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-[#6B35B8]/[0.04] rounded-full blur-[100px] pointer-events-none" />

      {/* Logo */}
      <div className="mb-16 flex flex-col items-center relative z-10" style={{ gap: 6 }}>
        <div className="flex items-center" style={{ gap: 2 }}>
          <img
            src="/logo-zephyr.png"
            alt="Zephyr OS"
            style={{ width: 52, height: 52, clipPath: 'url(#squircle)', flexShrink: 0 }}
          />
          <h1 style={{ fontFamily: 'system-ui, sans-serif', fontSize: 36, fontWeight: 300,
            letterSpacing: '0.18em', textTransform: 'uppercase', color: '#f0f0f5', lineHeight: 1 }}>
            EPHYR OS
          </h1>
        </div>
        <p style={{ fontFamily: 'system-ui, sans-serif', fontSize: 11, fontWeight: 300,
          letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(240,240,245,0.42)', margin: 0 }}>
          Awakening Interface
        </p>
        <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: 9, fontWeight: 300,
          letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(240,240,245,0.22)' }}>
          AI
        </span>
      </div>

      {/* Passphrase form */}
      <form onSubmit={handleSubmit} className="w-full max-w-xs relative z-10" data-testid="auth-form">
        <p className="text-center text-[#b0b4c0] text-sm mb-8 font-light tracking-wide">
          {isSetup ? 'Enter passphrase' : 'Set passphrase'}
        </p>

        <input
          data-testid="pin-input"
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          className="w-full bg-transparent text-center text-lg text-[#f0f0f5] border-b border-white/[0.06] focus:border-[#6B35B8] outline-none py-4 transition-colors duration-500 tracking-widest placeholder:tracking-normal placeholder:text-[#454860]"
          autoFocus
          autoComplete="current-password"
          placeholder="············"
        />

        <button
          data-testid="auth-submit"
          type="submit"
          disabled={!passphrase.trim() || loading}
          className="w-full mt-8 py-3 bg-[#6B35B8] text-white rounded-full font-light tracking-wider text-sm hover:bg-[#6B35B8]/90 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-500 shadow-[0_0_20px_rgba(107,53,184,0.25)]"
        >
          {loading ? '···' : 'Enter'}
        </button>
      </form>

      <div className="mt-12 flex gap-6">
        <a href="mailto:mail@wernerdutoit.co.za" className="text-[#b0b4c0]/70 text-xs font-mono tracking-wider hover:text-[#9B6DD4] transition-colors duration-500">
          contact me
        </a>
        <a href="https://wernerdutoit.co.za/gallery" target="_blank" rel="noopener noreferrer" className="text-[#b0b4c0]/70 text-xs font-mono tracking-wider hover:text-[#9B6DD4] transition-colors duration-500">
          gallery
        </a>
      </div>
    </div>
  );
}
